/* eslint-disable react/no-multi-comp */
import React from 'react'
import {
  Divider,
  Input,
  ListSubheader,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'

import {
  SelectEntity,
  TimezoneConstant,
  TimezoneGroupConstant,
} from '../../types'
import { getTranslatedFieldName } from '../../utils/languageUtils'
import Text from '../Text'
import PuiSelect from './PuiSelect'

const useStyles = makeStyles(
  theme => ({
    subHeader: {
      padding: theme.spacing(0),
    },
    subHeaderText: {
      lineHeight: theme.spacing(3),
      padding: theme.spacing(0, 1),
    },
    divider: {
      marginBottom: theme.spacing(1),
    },
  }),
  { name: 'TimezoneSelect' },
)

export type TimezoneSelectProps = SelectProps<string> & {
  readonly timezoneGroups: TimezoneGroupConstant[]
  readonly timezones: TimezoneConstant[]
  readonly useTimezoneGroupVariant: boolean
}

const TimezoneSelect = ({
  value: valueProp,
  onChange,
  timezoneGroups,
  timezones,
  useTimezoneGroupVariant,
  ...rest
}: TimezoneSelectProps) => {
  const classes = useStyles()
  const timezonesList = timezones.map(({ timezone }) => ({ name: timezone }))

  const allPossibleValues = timezoneGroups
    .flatMap(R.propOr([], 'timezones'))
    .map(R.prop('timezone'))
  const isValidValue = R.includes(valueProp, allPossibleValues)
  const value = isValidValue ? valueProp : ''

  return useTimezoneGroupVariant ? (
    <Select
      id="label-select-timezone-group"
      value={value}
      onChange={onChange}
      {...rest}
    >
      {timezoneGroups.map((group, idx) => [
        <ListSubheader
          className={classes.subHeader}
          key={getTranslatedFieldName(group)}
        >
          {idx > 0 && <Divider className={classes.divider} />}
          <Text className={classes.subHeaderText} variant="subheading4">
            {getTranslatedFieldName(group)}
          </Text>
        </ListSubheader>,
        ...(group?.timezones || []).map(item => (
          <MenuItem key={item.timezone} value={item.timezone}>
            {getTranslatedFieldName(item)}
          </MenuItem>
        )),
      ])}
    </Select>
  ) : (
    <PuiSelect
      fullWidth
      input={<Input id="timezone-select" />}
      items={timezonesList as SelectEntity[]}
      keyProp="name"
      value={value}
      onChange={(it, child) =>
        onChange?.(it as SelectChangeEvent<string>, child)
      }
    />
  )
}

export default TimezoneSelect
